import type { ComponentPropsWithRef, ReactNode } from 'react';
import * as Slot from 'radix-ui/slot';
import { cx } from '../../utils/cx';

export interface CardProps extends ComponentPropsWithRef<'div'> {
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Hover lift and focus ring, for cards that are links or buttons (use with `asChild`). */
  interactive?: boolean;
  asChild?: boolean;
}

export function Card({ padding = 'md', interactive, asChild, className, ...rest }: CardProps) {
  const Comp = asChild ? Slot.Root : 'div';
  return (
    <Comp
      className={cx('kd-card', className)}
      data-padding={padding}
      data-interactive={interactive || undefined}
      {...rest}
    />
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned (inline-end) slot for a button or menu. */
  action?: ReactNode;
  /** Heading level for the title; pick the one that fits the page outline. */
  level?: 2 | 3 | 4;
  className?: string;
}

export function CardHeader({ title, description, action, level = 3, className }: CardHeaderProps) {
  const Heading = `h${level}` as const;
  return (
    <div className={cx('kd-card__header', className)}>
      <div className="kd-card__heading">
        <Heading className="kd-card__title">{title}</Heading>
        {description && <p className="kd-card__description">{description}</p>}
      </div>
      {action && <div className="kd-card__action">{action}</div>}
    </div>
  );
}

export function CardFooter({ className, ...rest }: ComponentPropsWithRef<'div'>) {
  return <div className={cx('kd-card__footer', className)} {...rest} />;
}
